<template>
    <div class="table-color-filter">
        <div class="filter-conditions">
            <div
                class="filter-row"
                v-for="(condition, index) in clone"
                :key="`condition-${index}`"
            >
                <a-select
                    v-model="clone[index].color"
                    style="width: 250px; margin-right: 8px"
                >
                    <a-select-option
                        v-for="color in colors"
                        :key="color.name"
                        :value="color.code"
                    >
                        <div class="color-option">
                            <div class="color-box" :style="{ backgroundColor: color.code }"></div>
                            <span>{{ color.name }}</span>
                        </div>
                    </a-select-option>
                </a-select>

                <CustomPicker
                    v-model="clone[index].fieldId"
                    :items="fields"
                    idKey="_id"
                    labelKey="name"
                    :title="$t('message.field')"
                    style="width: 30%; margin-right: 8px"
                    @input="onFieldChange(condition, index)"
                />

                <CustomPicker
                    v-model="clone[index].operator"
                    :items="getOperators(condition)"
                    idKey="value"
                    labelKey="label"
                    :title="$t('tables.operator')"
                    style="width: 25%; margin-right: 8px"
                    :disabled="!condition.fieldId"
                    @input="onOperatorChange(condition, index)"
                />

                <component
                    :is="getValueInputComponent(condition)"
                    v-model="clone[index].value"
                    :options="getFieldOptions(condition)"
                    style="width: 30%; margin-right: 8px"
                    :placeholder="$t('message.value')"
                    :disabled="!condition.operator"
                ></component>

                <a-button type="danger" icon="delete" @click="removeCondition(index)" />
            </div>
        </div>

        <div class="filter-actions">
            <a-button type="dashed" icon="plus" @click="addCondition">{{ $t("tables.addCondition") }}</a-button>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { cloneDeep } from "lodash";
import { Input, Select } from "ant-design-vue";
import CustomPicker from "@/components/pickers/CustomPicker.vue";

export default Vue.extend({
    name: "TableColorFilter",
    components: {
        "a-input": Input,
        "a-select": Select,
        "a-select-option": Select.Option,
        CustomPicker,
    },
    props: {
        fields: {
            type: Array,
            required: true,
        },
        colorConditions: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            clone: [],
            colors: [
                { name: "Grass Green", code: "#81bfa5" },
                { name: "Done Green", code: "#80e3ba" },
                { name: "Bright Green", code: "#cde992" },
                { name: "Saladish", code: "#e4daa0" },
                { name: "Egg Yolk", code: "#ffe580" },
                { name: "Working Orange", code: "#fed59e" },
                { name: "Dark Orange", code: "#ffb196" },
                { name: "Peach", code: "#ffd6d6" },
                { name: "Sunset", code: "#ffbaba" },
                { name: "Stuck Red", code: "#f0a1ad" },
                { name: "Dark Red", code: "#dd99a9" },
                { name: "Sofia Pink", code: "#ff8ac4" },
                { name: "Lipstick", code: "#fface1" },
                { name: "Bubble", code: "#fcd0f8" },
                { name: "Purple", code: "#d0aeed" },
                { name: "Dark Purple", code: "#bba5e8" },
                { name: "Berry", code: "#be9dc4" },
                { name: "Dark Indigo", code: "#a08bc9" },
                { name: "Indigo", code: "#aaacef" },
                { name: "Navy", code: "#90a7c8" },
                { name: "Bright Blue", code: "#abcdfd" },
                { name: "Dark Blue", code: "#80c2df" },
                { name: "Aquamarine", code: "#a6e5e2" },
                { name: "Chili Blue", code: "#b2e5ff" },
                { name: "River", code: "#b3d0de" },
                { name: "Winter", code: "#ccd6de" },
                { name: "Explosive", code: "#e1e1e1" },
                { name: "American Gray", code: "#bfbfbf" },
                { name: "Blackish", code: "#999999" },
                { name: "Brown", code: "#bfa9a3" },
                { name: "Orchid", code: "#ecbad7" },
                { name: "Tan", code: "#d6cabc" },
                { name: "Sky", code: "#d0f1fa" },
                { name: "Coffee", code: "#dec0b7" },
                { name: "Royal", code: "#95bbf2" },
                { name: "Teal", code: "#8bacb1" },
                { name: "Lavender", code: "#ded4fc" },
                { name: "Steel", code: "#d4dff4" },
                { name: "Lilac", code: "#ceccdc" },
                { name: "Pecan", code: "#ab9f9f" }
            ],
        };
    },
    created() {
        this.clone = cloneDeep(this.colorConditions);
        if (this.clone.length === 0) {
            setTimeout(() => {
                this.addInitialCondition();
            }, 500);
        }
    },
    watch: {
        colorConditions: {
            handler(newValue) {
                const isChanged = !_.isEqual(newValue, this.clone);
                if (isChanged) {
                    this.clone = cloneDeep(newValue);
                    
                    if (this.clone.length === 0) {
                        this.addInitialCondition();
                    }
                }
            },
        },
        clone: {
            deep: true,
            handler(newValue) {
                console.log("[TableColorFilter] clone changed", newValue);
                const isChanged = !_.isEqual(newValue, this.colorConditions);
                if (isChanged) {
                    this.$emit("update-color-conditions", newValue);
                }
            },
        },
    },
    methods: {
        getRandomUnusedColor() {
            const usedColors = new Set(this.clone.map(condition => condition.color));
            const availableColors = this.colors.filter(color => !usedColors.has(color.code));
            if (availableColors.length === 0) return this.colors[0].code;
            return availableColors[Math.floor(Math.random() * availableColors.length)].code;
        },
        
        addInitialCondition() {
            const initialCondition = {
                color: this.getRandomUnusedColor(),
                fieldId: this.fields.length > 0 ? this.fields[0]._id : null,
                operator: null,
                value: null,
            };
            this.clone.push(initialCondition);
        },
        
        addCondition() {
            const newCondition = {
                color: this.getRandomUnusedColor(),
                fieldId: null,
                operator: null,
                value: null,
            };
            this.clone.push(newCondition);
        },
        removeCondition(index) {
            this.clone.splice(index, 1);
        },
        onOperatorChange(condition, index) {
            condition.value = null;
            this.clone[index] = cloneDeep(condition);
            this.clone = cloneDeep(this.clone);
        },
        onFieldChange(condition, index) {
            condition.operator = null;
            condition.value = null;
            this.clone[index] = cloneDeep(condition);
            this.clone = cloneDeep(this.clone);
        },
        getOperators(condition) {
            if (!condition.fieldId) return [];
            const field = this.fields.find((f) => f._id === condition.fieldId);
            const valueType = field.valueType;
            const operators = {
                string: [
                    { label: this.$t("message.contains"), value: "contains" },
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.startsWith"), value: "starts_with" },
                    { label: this.$t("message.endsWith"), value: "ends_with" },
                ],
                number: [
                    { label: this.$t("message.equals"), value: "equal" },
                    { label: this.$t("message.notEquals"), value: "not_equal" },
                    { label: this.$t("message.greaterThan"), value: "greater_than" },
                    { label: this.$t("message.lessThan"), value: "less_than" },
                ],
                date: [
                    { label: this.$t("message.before"), value: "before" },
                    { label: this.$t("message.after"), value: "after" },
                ],
                boolean: [
                    { label: this.$t("message.is"), value: "equal" },
                    { label: this.$t("message.isNot"), value: "not_equal" },
                ],
            };
            return operators[valueType] || [];
        },
        getValueInputComponent(condition) {
            if (!condition.fieldId) return "a-input";
            const field = this.fields.find((f) => f._id === condition.fieldId);
            const fieldType = field.fieldType;
            const valueType = field.valueType;

            if (fieldType === "checkbox" || valueType === "boolean") {
                return "a-select";
            } else if (fieldType === "dropdown" || fieldType === "select") {
                return "a-select";
            } else if (valueType === "date") {
                return "a-date-picker";
            } else {
                return "a-input";
            }
        },
        getFieldOptions(condition) {
            const field = this.fields.find((f) => f._id === condition.fieldId);
            if (!field) return [];

            if (field.fieldType === "checkbox" || field.valueType === "boolean") {
                return [
                    { label: "True", value: true },
                    { label: "False", value: false },
                ];
            } else if (field.fieldType === "dropdown" || field.fieldType === "select") {
                const options = this.$_.get(field, "options.dropdown.options", []) || [];
                return options.map((opt) => ({ label: opt.value, value: opt.value }));
            } else {
                return [];
            }
        },
    },
});
</script>

<style scoped lang="scss">
.table-color-filter {
    .filter-conditions {
        .filter-row {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            justify-content: space-between;
            gap: 10px;
        }
    }

    .filter-actions {
        margin-top: 16px;
    }
}

.color-option {
    display: flex;
    align-items: center;

    .color-box {
        width: 16px;
        height: 16px;
        margin-right: 8px;
        border-radius: 3px;
    }
}
</style> 